import React, { useState, useEffect } from 'react';
import { Mail, MessageCircle, Send, Eye, EyeOff, FileText, MapPin, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  getNotificationSettings, updateNotificationSettings, testEmailNotification, testWhatsappNotification,
  getUniversalCatalog, updateUniversalCatalog, uploadFile,
  getLocationSettings, updateLocationSettings
} from '../../services/api';
import { toast } from 'sonner';

const SettingsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const [showSmtpPass, setShowSmtpPass] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);
  const [catalogFile, setCatalogFile] = useState(null);
  const [catalogUrl, setCatalogUrl] = useState('');

  const [settings, setSettings] = useState({
    email_enabled: false, smtp_host: '', smtp_port: '587', smtp_email: '', smtp_password: '',
    notify_email: 'sales@namohtoolings.in',
    whatsapp_enabled: false, twilio_account_sid: '', twilio_auth_token: '',
    twilio_whatsapp_from: 'whatsapp:+14155238886', twilio_whatsapp_to: ''
  });

  const [location, setLocation] = useState({
    address_line1: 'K-237,238,PHASE VIII FOCAL POINT',
    address_line2: 'LUDHIANA, PUNJAB 141010(INDIA)',
    phone: '+91 78883 32031',
    email: 'sales@namohtoolings.in',
    map_embed_url: '',
    google_maps_link: ''
  });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [notifRes, catalogRes, locRes] = await Promise.all([
          getNotificationSettings().catch(() => ({ data: {} })),
          getUniversalCatalog().catch(() => ({ data: { catalog_url: null } })),
          getLocationSettings().catch(() => ({ data: {} }))
        ]);
        if (notifRes.data && Object.keys(notifRes.data).length > 0) setSettings(prev => ({ ...prev, ...notifRes.data }));
        if (catalogRes.data?.catalog_url) setCatalogUrl(catalogRes.data.catalog_url);
        if (locRes.data && Object.keys(locRes.data).length > 0) setLocation(prev => ({ ...prev, ...locRes.data }));
      } catch { /* defaults */ }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await updateNotificationSettings(settings);
      toast.success('Notification settings saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSaveCatalog = async () => {
    setSaving(true);
    try {
      let url = catalogUrl;
      if (catalogFile) {
        const res = await uploadFile(catalogFile, 'catalog');
        url = res.data.url;
        setCatalogUrl(url);
      }
      if (url) {
        await updateUniversalCatalog(url);
        toast.success('Universal catalog updated');
      } else {
        toast.error('Please upload a catalog PDF');
      }
    } catch (err) { toast.error('Failed to save catalog'); }
    finally { setSaving(false); setCatalogFile(null); }
  };

  const handleSaveLocation = async () => {
    setSaving(true);
    try {
      await updateLocationSettings(location);
      toast.success('Location settings saved');
    } catch (err) { toast.error('Failed to save location'); }
    finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try { await testEmailNotification(); toast.success('Test email sent!'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setTestingEmail(false); }
  };

  const handleTestWhatsapp = async () => {
    setTestingWhatsapp(true);
    try { await testWhatsappNotification(); toast.success('Test WhatsApp sent!'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setTestingWhatsapp(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-[#2E3A6F] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-admin">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500">Manage notifications, catalog, and location</p>
      </div>

      {/* Universal Catalog */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <FileText className="text-purple-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Universal Catalog</h3>
              <p className="text-xs text-gray-500">Upload the main product catalog PDF shown on website</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Upload Catalog PDF</Label>
              <Input type="file" accept=".pdf" onChange={(e) => setCatalogFile(e.target.files[0])} className="text-sm" data-testid="universal-catalog-upload" />
            </div>
            {catalogUrl && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Current:</span>
                <a href={catalogUrl.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${catalogUrl}` : catalogUrl} target="_blank" rel="noopener noreferrer" className="text-[#2E3A6F] underline text-xs">{catalogUrl}</a>
              </div>
            )}
            <Button onClick={handleSaveCatalog} disabled={saving} className="bg-[#2E3A6F]" data-testid="save-catalog-btn">
              <Upload size={14} className="mr-2" />{saving ? 'Saving...' : 'Save Catalog'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Location Settings */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <MapPin className="text-red-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Location & Contact</h3>
              <p className="text-xs text-gray-500">Update address, contact info & Google Maps embed</p>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Address Line 1</Label>
              <Input value={location.address_line1} onChange={(e) => setLocation({ ...location, address_line1: e.target.value })} data-testid="location-address1" />
            </div>
            <div>
              <Label>Address Line 2 (City, State, Pin)</Label>
              <Input value={location.address_line2} onChange={(e) => setLocation({ ...location, address_line2: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Phone</Label><Input value={location.phone} onChange={(e) => setLocation({ ...location, phone: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={location.email} onChange={(e) => setLocation({ ...location, email: e.target.value })} /></div>
            </div>
            <div>
              <Label>Google Maps Embed URL</Label>
              <Input value={location.map_embed_url} onChange={(e) => setLocation({ ...location, map_embed_url: e.target.value })} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="location-map-embed" />
              <p className="text-[10px] text-gray-400 mt-1">Go to Google Maps &gt; Share &gt; Embed a map &gt; Copy the src URL from the iframe code</p>
            </div>
            <div>
              <Label>Google Maps Link (for "Get Directions")</Label>
              <Input value={location.google_maps_link} onChange={(e) => setLocation({ ...location, google_maps_link: e.target.value })} placeholder="https://maps.google.com/..." />
            </div>
            {location.map_embed_url && (
              <div className="rounded-lg overflow-hidden border h-48">
                <iframe src={location.map_embed_url} width="100%" height="100%" style={{ border: 0 }} allowFullScreen loading="lazy" title="Location Map" />
              </div>
            )}
            <Button onClick={handleSaveLocation} disabled={saving} className="bg-[#2E3A6F]" data-testid="save-location-btn">
              {saving ? 'Saving...' : 'Save Location'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Mail className="text-blue-600" size={20} /></div>
              <div><h3 className="font-semibold text-gray-900">Email Notifications</h3><p className="text-xs text-gray-500">Send email on new enquiry</p></div>
            </div>
            <Switch checked={settings.email_enabled} onCheckedChange={(v) => setSettings({ ...settings, email_enabled: v })} data-testid="email-toggle" />
          </div>
          {settings.email_enabled && (
            <div className="space-y-4 pt-2 border-t">
              <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-800 space-y-1">
                <p className="font-semibold">SMTP Guide: Gmail: smtp.gmail.com:587 | Zoho: smtp.zoho.in:587 | Outlook: smtp.office365.com:587</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>SMTP Host *</Label><Input value={settings.smtp_host} onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host-input" /></div>
                <div><Label>SMTP Port</Label><Input value={settings.smtp_port} onChange={(e) => setSettings({ ...settings, smtp_port: e.target.value })} /></div>
              </div>
              <div><Label>SMTP Email *</Label><Input value={settings.smtp_email} onChange={(e) => setSettings({ ...settings, smtp_email: e.target.value })} placeholder="sales@namohtoolings.in" /></div>
              <div>
                <Label>SMTP Password *</Label>
                <div className="relative">
                  <Input type={showSmtpPass ? 'text' : 'password'} value={settings.smtp_password} onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })} />
                  <button type="button" onClick={() => setShowSmtpPass(!showSmtpPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showSmtpPass ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div><Label>Notify To</Label><Input value={settings.notify_email} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} /></div>
              <Button variant="outline" onClick={handleTestEmail} disabled={testingEmail} className="text-sm"><Send size={14} className="mr-2" />{testingEmail ? 'Sending...' : 'Test Email'}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* WhatsApp Notifications */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><MessageCircle className="text-green-600" size={20} /></div>
              <div><h3 className="font-semibold text-gray-900">WhatsApp Notifications</h3><p className="text-xs text-gray-500">Receive WhatsApp for new enquiries</p></div>
            </div>
            <Switch checked={settings.whatsapp_enabled} onCheckedChange={(v) => setSettings({ ...settings, whatsapp_enabled: v })} data-testid="whatsapp-toggle" />
          </div>
          {settings.whatsapp_enabled && (
            <div className="space-y-4 pt-2 border-t">
              <div className="bg-green-50 rounded-lg p-3 text-xs text-green-800">
                <p className="font-semibold">Setup: twilio.com &gt; Console &gt; Messaging &gt; WhatsApp Sandbox</p>
              </div>
              <div><Label>Account SID *</Label><Input value={settings.twilio_account_sid} onChange={(e) => setSettings({ ...settings, twilio_account_sid: e.target.value })} placeholder="AC..." /></div>
              <div>
                <Label>Auth Token *</Label>
                <div className="relative">
                  <Input type={showTwilioToken ? 'text' : 'password'} value={settings.twilio_auth_token} onChange={(e) => setSettings({ ...settings, twilio_auth_token: e.target.value })} />
                  <button type="button" onClick={() => setShowTwilioToken(!showTwilioToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showTwilioToken ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>From (Twilio No.)</Label><Input value={settings.twilio_whatsapp_from} onChange={(e) => setSettings({ ...settings, twilio_whatsapp_from: e.target.value })} /></div>
                <div><Label>Your WhatsApp No. *</Label><Input value={settings.twilio_whatsapp_to} onChange={(e) => setSettings({ ...settings, twilio_whatsapp_to: e.target.value })} placeholder="+919876543210" /></div>
              </div>
              <Button variant="outline" onClick={handleTestWhatsapp} disabled={testingWhatsapp} className="text-sm"><Send size={14} className="mr-2" />{testingWhatsapp ? 'Sending...' : 'Test WhatsApp'}</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSaveNotifications} disabled={saving} className="bg-[#2E3A6F] px-8" data-testid="save-settings-btn">
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </Button>
      </div>
    </div>
  );
};

export default SettingsAdmin;
